import { useEffect, useRef, useState } from 'react';
import { Task, MosyUser, TaskStatus } from '../../lib/types';
import { Checkbox } from '../ui/Checkbox';
import { Chip } from '../ui/Chip';
import { Icon } from '../ui/Icon';
import { InlineAssignees } from './InlineAssignees';
import { fmtShort, daysFromToday } from '../../lib/dates';
import { CATEGORY_COLOR } from '../../lib/categoryColors';
import { TaskName } from './TaskName';

interface Props {
  task: Task;
  team: MosyUser[];
  onToggle: () => void;
  onAssign: (ids: number[]) => void;
  onCampaignClick?: (id: string) => void;
  onEdit?: (task: Task) => void;
  onDelete?: (task: Task) => void;
  campaignName?: string;
  campaignType?: string;
  showCampaign?: boolean;
}

const previousNonDone = (t: Task): TaskStatus =>
  t.status === 'Done' ? 'In progress' : t.status;

const RowMenu = ({
  onEdit,
  onDelete,
}: {
  onEdit?: () => void;
  onDelete?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="icon-btn"
        aria-label="Task actions"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <Icon name="more" size={14} />
      </button>
      {open && (
        <div className="popover" style={{ right: 0, top: 30, minWidth: 160 }}>
          {onEdit && (
            <button
              type="button"
              className="popover-item"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onEdit();
              }}
            >
              <Icon name="settings" size={13} /> Edit
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              className="popover-item"
              style={{ color: 'var(--warn)' }}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onDelete();
              }}
            >
              <Icon name="x" size={13} /> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const TaskRow = ({
  task,
  team,
  onToggle,
  onAssign,
  onCampaignClick,
  onEdit,
  onDelete,
  campaignName,
  campaignType,
  showCampaign = true,
}: Props) => {
  const overdue = daysFromToday(task.due_date) < 0 && task.status !== 'Done';
  const typeColor = campaignType === 'Promotion' ? 'var(--sitewide)'
    : campaignType === 'Brand' ? 'var(--brand)'
    : campaignType === 'Theme' ? 'var(--seasonal)' : 'var(--ink-5)';

  const showMenu = !!(onEdit || onDelete);
  const gridColumns = showCampaign
    ? `18px 1fr 1fr 110px 90px 92px 80px${showMenu ? ' 28px' : ''}`
    : `18px 1fr 110px 90px 92px 80px${showMenu ? ' 28px' : ''}`;
  // A task due after its placement goes live is late by definition — worth
  // flagging, since that's exactly the urgency this column is here to show.
  const prepLate = !!task.asset_publish_date
    && task.status !== 'Done'
    && task.due_date > task.asset_publish_date;

  return (
    <div
      className={`task-row ${task.status === 'Done' ? 'done' : ''}`}
      style={{ gridTemplateColumns: gridColumns }}
    >
      <Checkbox
        checked={task.status === 'Done'}
        onChange={onToggle}
        onClick={(e) => e.stopPropagation()}
      />
      <div className="task-name" style={{ fontSize: 12.8, color: 'var(--ink)' }}>
        <TaskName name={task.name} />
        {task.asset_title && (
          <span
            style={{
              marginLeft: 8,
              fontSize: 10.5,
              color: 'var(--ink-4)',
              background: 'var(--bg-subtle)',
              padding: '1px 6px',
              borderRadius: 3,
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
            title={`Linked to asset: ${task.asset_title}${task.asset_category ? ` (${task.asset_category})` : ''}`}
          >
            {task.asset_category && (
              <span
                style={{
                  display: 'inline-block',
                  width: 6, height: 6, borderRadius: 50,
                  background: CATEGORY_COLOR[task.asset_category],
                  marginRight: 5,
                  verticalAlign: 'middle',
                }}
              />
            )}
            ↗ {task.asset_title.length > 28 ? task.asset_title.slice(0, 28) + '…' : task.asset_title}
          </span>
        )}
      </div>
      {showCampaign && (
        task.campaign ? (
          <button
            type="button"
            className="link"
            style={{ fontSize: 13, textAlign: 'left' }}
            onClick={(e) => {
              e.stopPropagation();
              onCampaignClick?.(task.campaign!);
            }}
          >
            <span className="dot" style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 50, background: typeColor, marginRight: 6 }} />
            {campaignName || task.campaign}
          </button>
        ) : (
          <span style={{ fontSize: 13, color: 'var(--ink-4)', fontStyle: 'italic' }}>
            Standalone
          </span>
        )
      )}
      <Chip kind="status" value={task.status} />
      <div
        className="mono"
        style={{
          fontSize: 11.5,
          color: overdue ? 'var(--warn)' : 'var(--ink-3)',
          textAlign: 'right',
        }}
      >
        {fmtShort(task.due_date)}
      </div>
      <div
        className="mono"
        style={{
          fontSize: 11,
          color: prepLate ? 'var(--warn)' : 'var(--ink-4)',
          textAlign: 'right',
          whiteSpace: 'nowrap',
        }}
        title={
          task.asset_publish_date
            ? `The placement goes live on ${fmtShort(task.asset_publish_date)}${
              prepLate ? ' — before this task is due' : ''}`
            : undefined
        }
      >
        {task.asset_publish_date ? `live ${fmtShort(task.asset_publish_date)}` : ''}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <InlineAssignees
          assignees={task.assignee_details ?? []}
          team={team}
          onChange={onAssign}
          anchorRight
        />
      </div>
      {showMenu && (
        <RowMenu
          onEdit={onEdit ? () => onEdit(task) : undefined}
          onDelete={onDelete ? () => onDelete(task) : undefined}
        />
      )}
    </div>
  );
};

export { previousNonDone };
export default TaskRow;
